import { Field, Formik } from 'formik';
import { Pencil, CaretDown, XCircle } from 'phosphor-react/dist';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Tab,
  Tabs,
} from 'react-bootstrap';
import { FaCogs, FaSave, FaUser } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import MoneyField from '~/Components/Form/MoneyField';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { Selector } from '~/Components/Prospectos/ProspectoDetalhes/Pesquisa/Form';
import {
  Linha,
  Avatar,
  OpenSansMuted,
  Nome,
} from '~/Components/Prospectos/ProspectoItem/styles';
import {
  buscarParametrosCorretores,
  buscarParametrosEmpreendimentos,
  deletarParametro,
  inserirOuAtualizarParametrosCorretor,
  inserirOuAtualizarParametrosEmpreendimento,
} from '~/Services/Service';
import { formatarMonetario } from '~/Utils/Helpers';

export default function IndicacaoParametros() {
  const [loading, setLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('empreendimento');
  const [parametrosEmp, setParametrosEmp] = useState([]);
  const [parametrosCorretor, setParametrosCorretor] = useState([]);
  const [isEditando, setIsEditando] = useState(false);
  const [empFormData, setEmpFormData] = useState(null);
  const [corretorFormData, setCorretorFormData] = useState(null);
  const [showConfirmacao, setShowConfirmacao] = useState(false);
  const [deletarContexto, setDeletarContexto] = useState(null);
  const [deletarCodigo, setDeletarCodigo] = useState(null);
  const empreendimentos = useSelector(state => state.empreendimentos);
  const corretores = useSelector(state => state.corretor);

  const corretoresOptions = useMemo(
    () =>
      (corretores.listaCorretores || []).map(item => ({
        label: item.nome,
        value: item.codigo,
      })),
    [corretores.listaCorretores]
  );

  const empreendimentosOptions = useMemo(
    () =>
      (empreendimentos.list || []).map(item => ({
        label: item.descricao,
        value: item.codigo,
      })),
    [empreendimentos.list]
  );

  const fetchParametrosEmpreendimento = useCallback(async () => {
    try {
      setLoading(true);
      const response = await buscarParametrosEmpreendimentos();
      setParametrosEmp(response);
    } catch (err) {
      console.error(err);
      toast.error(
        'Falha ao carregar parâmetros de empreendimentos. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchParametrosCorretores = useCallback(async () => {
    try {
      setLoading(true);
      const response = await buscarParametrosCorretores();
      setParametrosCorretor(response);
    } catch (err) {
      console.error(err);
      toast.error(
        'Falha ao carregar parâmetros de corretores. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParametrosEmpreendimento();
    fetchParametrosCorretores();
  }, [fetchParametrosEmpreendimento, fetchParametrosCorretores]);

  function getInitials(nome) {
    if (!nome) return '';
    return nome
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 1)
      .toUpperCase();
  }

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [detalhesAbertos, setDetalhesAbertos] = useState({
    corretores: {},
    empreendimentos: {},
  });

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth <= 768);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleDetalhesCorretor = id => {
    setDetalhesAbertos(prev => ({
      ...prev,
      corretores: { ...prev.corretores, [id]: !prev.corretores[id] },
    }));
  };

  const toggleDetalhesEmp = id => {
    setDetalhesAbertos(prev => ({
      ...prev,
      empreendimentos: {
        ...prev.empreendimentos,
        [id]: !prev.empreendimentos[id],
      },
    }));
  };

  if (
    loading ||
    !empreendimentos.list.length ||
    !corretores.listaCorretores.length
  ) {
    return <Loading />;
  }

  const byEmpreendimentoSchema = Yup.object({
    codigo_familia: Yup.string().required('Campo obrigatório'),
    comissao_promotor_externo: Yup.number()
      .positive()
      .required('Campo obrigatório'),
    comissao_corretor: Yup.number()
      .positive()
      .required('Campo obrigatório'),
    valor_desconto_cliente: Yup.number()
      .nullable()
      .transform(value => (value === '' ? null : value))
      .typeError('Campo obrigatório')
      .required('Campo obrigatório')
      .positive('Campo obrigatório'),
    is_brinde: Yup.boolean().required('Campo obrigatório'),
  });

  const byEmpreendimentoInitialValues = {
    comissao_promotor_externo: 200,
    comissao_corretor: 80,
    codigo_familia: '',
    valor_desconto_cliente: '',
    is_brinde: false,
  };

  const byCorretoresSchema = Yup.object({
    codigo_corretor: Yup.string().required('Campo obrigatório'),
    dias_sem_atendimento: Yup.number()
      .integer()
      .positive()
      .required('Campo obrigatório'),
    qtd_fidelizados: Yup.number()
      .integer()
      .positive()
      .required('Campo obrigatório'),
    tempo_sem_atendimento: Yup.number()
      .integer()
      .positive('Campo obrigatório')
      .required('Campo obrigatório'),
  });

  const byCorretoresInitialValues = {
    codigo_corretor: '',
    dias_sem_atendimento: 90,
    qtd_fidelizados: 2,
    tempo_sem_atendimento: 30,
  };

  const onSubmitEmpreendimento = async values => {
    try {
      await inserirOuAtualizarParametrosEmpreendimento({
        ...values,
        is_brinde: values.is_brinde ? 1 : 0,
      });
      toast.success('Parâmetros atualizados com sucesso!');
      setIsEditando(false);
      setEmpFormData(null);
      await fetchParametrosEmpreendimento();
    } catch (error) {
      console.log(error);
      toast.error('Não foi possível salvar os parâmetros do empreendimento.');
    }
  };

  const onSubmitCorretores = async values => {
    try {
      await inserirOuAtualizarParametrosCorretor(values);
      toast.success('Parâmetros atualizados com sucesso!');
      setIsEditando(false);
      setCorretorFormData(null);
      await fetchParametrosCorretores();
    } catch (error) {
      console.log(error);
      toast.error('Não foi possível salvar os parâmetros do corretor.');
    }
  };

  const handleDelete = async () => {
    try {
      await deletarParametro(deletarCodigo, deletarContexto);
      toast.success('Parâmetro deletado com sucesso!');
      setShowConfirmacao(false);
      setDeletarContexto(null);
      setDeletarCodigo(null);
      await fetchParametrosEmpreendimento();
      await fetchParametrosCorretores();
    } catch (error) {
      console.log(error);
      toast.error('Não foi possível excluir o parâmetro.');
    }
  };

  return (
    <>
      <Tabs
        activeKey={activeTabKey}
        onSelect={k => {
          setActiveTabKey(k);
          setIsEditando(false);
          setEmpFormData(null);
          setCorretorFormData(null);
        }}
        id="indicacao-tabs"
        className="mb-3"
      >
        <Tab eventKey="empreendimento" title="Empreendimento">
          <Card>
            <Card.Header className="bg-light border-0 d-flex align-items-center gap-3">
              <FaCogs className="text-primary" />
              <Card.Title className="mb-0">
                Parâmetros por Empreendimento
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Formik
                initialValues={empFormData || byEmpreendimentoInitialValues}
                validationSchema={byEmpreendimentoSchema}
                onSubmit={onSubmitEmpreendimento}
                enableReinitialize
              >
                {({
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  handleSubmit,
                  isSubmitting,
                  setFieldTouched,
                }) => {
                  return (
                    <form onSubmit={handleSubmit}>
                      <Row className="align-items-start">
                        <Col md={3}>
                          <Form.Group>
                            <Selector
                              label="Empreendimento"
                              noOptionsMessage={() =>
                                'Nenhum empreendimento encontrado'
                              }
                              placeholder="Pesquisar empreendimentos..."
                              name="codigo_familia"
                              options={empreendimentosOptions}
                              onChange={selectedOption => {
                                setFieldValue(
                                  'codigo_familia',
                                  selectedOption ? selectedOption.value : ''
                                );
                                setFieldTouched('codigo_familia', true, true);
                              }}
                              onBlur={() =>
                                setFieldTouched('codigo_familia', true, true)
                              }
                              value={
                                empreendimentosOptions.find(
                                  opt => opt.value === values.codigo_familia
                                ) || null
                              }
                            />
                            {touched.codigo_familia && errors.codigo_familia && (
                              <div
                                className="text-danger"
                                style={{ fontSize: '0.875rem' }}
                              >
                                {errors.codigo_familia}
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                        <Col md={'auto'}>
                          <Form.Group>
                            <Form.Label>
                              Comissão do Promotor Externo
                            </Form.Label>
                            <Field
                              name="comissao_promotor_externo"
                              component={MoneyField}
                              className={`form-control ${
                                touched.comissao_promotor_externo &&
                                errors.comissao_promotor_externo
                                  ? 'is-invalid'
                                  : ''
                              }`}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.comissao_promotor_externo}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={'auto'}>
                          <Form.Group>
                            <Form.Label>
                              Comissão do Colaborador Interno
                            </Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="number"
                                name="comissao_corretor"
                                min={0}
                                max={100}
                                placeholder="Ex.: 80"
                                value={values.comissao_corretor}
                                onChange={e =>
                                  setFieldValue(
                                    'comissao_corretor',
                                    Number(e.currentTarget.value)
                                  )
                                }
                                isInvalid={
                                  touched.comissao_corretor &&
                                  errors.comissao_corretor
                                }
                              />
                              <InputGroup.Text>%</InputGroup.Text>
                            </InputGroup>
                            <Form.Control.Feedback type="invalid">
                              {errors.comissao_corretor}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={'auto'}>
                          <Form.Group>
                            <Form.Label>Valor Desconto Cliente</Form.Label>
                            <Field
                              name="valor_desconto_cliente"
                              component={MoneyField}
                              className={`form-control ${
                                touched.valor_desconto_cliente &&
                                errors.valor_desconto_cliente
                                  ? 'is-invalid'
                                  : ''
                              }`}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.valor_desconto_cliente}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={'auto'}>
                          <Form.Group>
                            <Form.Label htmlFor="is_brinde">Brinde?</Form.Label>
                            <Form.Check
                              id="is_brinde"
                              type="checkbox"
                              checked={values.is_brinde}
                              onChange={e =>
                                setFieldValue(
                                  'is_brinde',
                                  e.currentTarget.checked
                                )
                              }
                              className="mr-2 mt-1"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.valor_desconto_cliente}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col className="text-end  mt-3">
                          <BotaoSalvar isSubmitting={isSubmitting} />
                        </Col>
                      </Row>
                    </form>
                  );
                }}
              </Formik>
            </Card.Body>
          </Card>
          {parametrosEmp.length > 0 &&
            (isMobile ? (
              <div>
                {parametrosEmp.map(item => {
                  const aberto = !!detalhesAbertos.empreendimentos[
                    item.codigo_familia
                  ];
                  return (
                    <Card
                      key={item.codigo_familia}
                      style={{
                        border: '1px solid #f0f0f0',
                        boxShadow: aberto
                          ? '0 0 0 2px #2196f3'
                          : '0 1px 2px rgba(44,62,80,0.04)',
                        padding: 12,
                        marginBottom: 8,
                        background: '#fff',
                        fontFamily: "'Open Sans', Arial, sans-serif",
                      }}
                    >
                      <div className="d-flex align-items-center w-100">
                        <Avatar style={{ marginRight: 12 }}>
                          {getInitials(item.nome_da_familia)}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                          <Nome style={{ fontWeight: 600, fontSize: '1rem' }}>
                            {item.nome_da_familia}
                          </Nome>
                          <OpenSansMuted style={{ fontSize: '0.7rem' }}>
                            Empreendimento
                          </OpenSansMuted>
                        </div>
                        <button
                          onClick={() => toggleDetalhesEmp(item.codigo_familia)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            marginLeft: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'transform 0.5s',
                          }}
                          aria-label={
                            aberto ? 'Ocultar detalhes' : 'Exibir detalhes'
                          }
                        >
                          <CaretDown
                            size={22}
                            style={{
                              transform: aberto
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                              transition: 'transform 0.5s',
                              color: '#293c66',
                            }}
                          />
                        </button>
                      </div>
                      {aberto && (
                        <>
                          <hr />
                          <div
                            className="d-flex flex-column w-100"
                            style={{ gap: 24 }}
                          >
                            <div style={{ flex: 1 }}>
                              <OpenSansMuted
                                style={{ fontSize: '0.8rem', marginBottom: 2 }}
                              >
                                Comissão Promotor
                              </OpenSansMuted>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: '#293c66',
                                  fontSize: '1rem',
                                }}
                              >
                                {formatarMonetario(
                                  item.comissao_promotor_externo
                                )}
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <OpenSansMuted
                                style={{ fontSize: '0.8rem', marginBottom: 2 }}
                              >
                                Comissão Colaborador Interno
                              </OpenSansMuted>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: '#293c66',
                                  fontSize: '1rem',
                                }}
                              >
                                {item.comissao_colaborador_interno}%
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <OpenSansMuted
                                style={{ fontSize: '0.8rem', marginBottom: 2 }}
                              >
                                Valor Desconto Cliente
                              </OpenSansMuted>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: '#293c66',
                                  fontSize: '1rem',
                                }}
                              >
                                {formatarMonetario(item.valor_desconto_cliente)}
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <OpenSansMuted
                                style={{ fontSize: '0.8rem', marginBottom: 2 }}
                              >
                                Brinde?
                              </OpenSansMuted>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: '#293c66',
                                  fontSize: '1rem',
                                }}
                              >
                                {item.tem_brinde == 1 ? 'Sim' : 'Não'}
                              </div>
                            </div>
                          </div>
                          <div
                            className="row align-self-end mt-3 mr-2"
                            style={{ gap: 12 }}
                          >
                            <BotaoEditar
                              isEditando={isEditando}
                              onClick={() => {
                                setIsEditando(true);
                                setEmpFormData({
                                  codigo_familia: item.codigo_familia,
                                  comissao_promotor_externo: Number(
                                    item.comissao_promotor_externo == null
                                      ? 0
                                      : item.comissao_promotor_externo
                                  ),
                                  comissao_corretor: Number(
                                    item.comissao_colaborador_interno == null
                                      ? 0
                                      : item.comissao_colaborador_interno
                                  ),
                                  valor_desconto_cliente: Number(
                                    item.valor_desconto_cliente == null
                                      ? 0
                                      : item.valor_desconto_cliente
                                  ),
                                  is_brinde: item.tem_brinde == 1,
                                });
                              }}
                            />
                            <BotaoExcluir
                              isEditando={isEditando}
                              onClick={() => {
                                setDeletarContexto('empreendimento');
                                setDeletarCodigo(item.codigo);
                                setShowConfirmacao(true);
                              }}
                            />
                          </div>
                        </>
                      )}
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div>
                {parametrosEmp.map(item => (
                  <Linha
                    key={item.codigo_familia}
                    style={{ alignItems: 'center' }}
                  >
                    <div className="col-1">
                      <Avatar>{getInitials(item.nome_da_familia)}</Avatar>
                    </div>
                    <div className="col-3">
                      <div className="row">
                        <OpenSansMuted>Empreendimento</OpenSansMuted>
                      </div>
                      <div className="row">
                        <div style={{ fontWeight: 600 }}>
                          {item.nome_da_familia}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-auto">
                      <OpenSansMuted>Comissão Promotor</OpenSansMuted>
                      <div style={{ fontWeight: 600, color: '#293c66' }}>
                        {formatarMonetario(item.comissao_promotor_externo)}
                      </div>
                    </div>
                    <div className="col-md-auto">
                      <OpenSansMuted>
                        Comissão Colaborador Interno
                      </OpenSansMuted>
                      <div style={{ fontWeight: 600, color: '#293c66' }}>
                        {item.comissao_colaborador_interno}%
                      </div>
                    </div>
                    <div className="col-md-auto">
                      <OpenSansMuted>Valor Desconto Cliente</OpenSansMuted>
                      <div style={{ fontWeight: 600, color: '#293c66' }}>
                        {formatarMonetario(item.valor_desconto_cliente)}
                      </div>
                    </div>
                    <div className="col-md-auto">
                      <OpenSansMuted>Brinde?</OpenSansMuted>
                      <div style={{ fontWeight: 600, color: '#293c66' }}>
                        {item.tem_brinde == 1 ? 'Sim' : 'Não'}
                      </div>
                    </div>
                    <div className="col-md-auto">
                      <div
                        className="row"
                        style={{
                          display: 'flex',
                          gap: '6px',
                          alignItems: 'center',
                        }}
                      >
                        <BotaoEditar
                          isEditando={isEditando}
                          onClick={() => {
                            setIsEditando(true);
                            setEmpFormData({
                              codigo_familia: item.codigo_familia,
                              comissao_promotor_externo: Number(
                                item.comissao_promotor_externo == null
                                  ? 0
                                  : item.comissao_promotor_externo
                              ),
                              comissao_corretor: Number(
                                item.comissao_colaborador_interno == null
                                  ? 0
                                  : item.comissao_colaborador_interno
                              ),
                              valor_desconto_cliente: Number(
                                item.valor_desconto_cliente == null
                                  ? 0
                                  : item.valor_desconto_cliente
                              ),
                              is_brinde: item.tem_brinde == 1,
                            });
                          }}
                        />
                        <BotaoExcluir
                          isEditando={isEditando}
                          onClick={() => {
                            setDeletarContexto('empreendimento');
                            setDeletarCodigo(item.codigo);
                            setShowConfirmacao(true);
                          }}
                        />
                      </div>
                    </div>
                  </Linha>
                ))}
              </div>
            ))}
        </Tab>
        <Tab eventKey="corretor" title="Corretor">
          <Card>
            <Card.Header className="bg-light border-0 d-flex align-items-center gap-3">
              <FaUser className="text-primary" />
              <Card.Title className="mb-0">Parâmetros por Corretor</Card.Title>
            </Card.Header>
            <Card.Body>
              <Formik
                initialValues={corretorFormData || byCorretoresInitialValues}
                validationSchema={byCorretoresSchema}
                onSubmit={onSubmitCorretores}
                enableReinitialize
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                  setFieldTouched,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={3}>
                        <Form.Group>
                          <Selector
                            label="Corretor"
                            noOptionsMessage={() =>
                              'Nenhum corretor encontrado'
                            }
                            placeholder="Pesquisar corretores..."
                            name="codigo_corretor"
                            options={corretoresOptions}
                            onChange={selectedOption => {
                              setFieldValue(
                                'codigo_corretor',
                                selectedOption ? selectedOption.value : ''
                              );
                              setFieldTouched('codigo_corretor', true, true);
                            }}
                            onBlur={() =>
                              setFieldTouched('codigo_corretor', true, true)
                            }
                            value={
                              corretoresOptions.find(
                                opt => opt.value === values.codigo_corretor
                              ) || null
                            }
                          />
                          {touched.codigo_corretor && errors.codigo_corretor && (
                            <div
                              className="text-danger"
                              style={{ fontSize: '0.875rem' }}
                            >
                              {errors.codigo_corretor}
                            </div>
                          )}

                          <Form.Text className="text-muted">
                            Selecione o corretor para aplicar as regras.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Dias sem Atendimento</Form.Label>
                          <Form.Control
                            type="number"
                            name="dias_sem_atendimento"
                            value={values.dias_sem_atendimento}
                            min={0}
                            placeholder="Ex.: 90"
                            onChange={e =>
                              setFieldValue(
                                'dias_sem_atendimento',
                                Number(e.target.value)
                              )
                            }
                            onBlur={handleBlur}
                            isInvalid={
                              touched.dias_sem_atendimento &&
                              !!errors.dias_sem_atendimento
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.dias_sem_atendimento}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            Quantidade de dias sem atendimento antes do lead ir
                            para a roleta.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>
                            Quantidade Máxima de Fidelizações
                          </Form.Label>
                          <Form.Control
                            type="number"
                            name="qtd_fidelizados"
                            value={values.qtd_fidelizados}
                            min={0}
                            placeholder="Ex.: 2"
                            onChange={e =>
                              setFieldValue(
                                'qtd_fidelizados',
                                Number(e.target.value)
                              )
                            }
                            onBlur={handleBlur}
                            isInvalid={
                              touched.qtd_fidelizados &&
                              !!errors.qtd_fidelizados
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.qtd_fidelizados}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            Máximo de leads fidelizados permitidos.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Tempo sem Atendimento</Form.Label>
                          <Form.Control
                            type="number"
                            name="tempo_sem_atendimento"
                            value={values.tempo_sem_atendimento}
                            min={0}
                            placeholder="Ex.: 30"
                            onChange={e =>
                              setFieldValue(
                                'tempo_sem_atendimento',
                                Number(e.target.value)
                              )
                            }
                            onBlur={handleBlur}
                            isInvalid={
                              touched.tempo_sem_atendimento &&
                              !!errors.tempo_sem_atendimento
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.tempo_sem_atendimento}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            Tempo (em minutos) que o lead pode ficar sem
                            resposta antes de ir para a roleta.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="text-end">
                        <BotaoSalvar isSubmitting={isSubmitting} />
                      </Col>
                    </Row>
                  </form>
                )}
              </Formik>
            </Card.Body>
          </Card>
          {parametrosCorretor.length > 0 &&
            (isMobile ? (
              <div>
                {parametrosCorretor.map(item => {
                  const aberto = !!detalhesAbertos.corretores[
                    item.codigo_corretor
                  ];
                  return (
                    <Card
                      key={item.codigo_corretor}
                      style={{
                        border: '1px solid #f0f0f0',
                        boxShadow: aberto
                          ? '0 0 0 2px #2196f3'
                          : '0 1px 2px rgba(44,62,80,0.04)',
                        padding: 12,
                        marginBottom: 8,
                        background: '#fff',
                        fontFamily: "'Open Sans', Arial, sans-serif",
                      }}
                    >
                      <div className="d-flex align-items-center w-100">
                        <Avatar style={{ marginRight: 12 }}>
                          {getInitials(item.nome_corretor)}
                        </Avatar>
                        <div style={{ flex: 1 }}>
                          <Nome style={{ fontWeight: 600, fontSize: '1rem' }}>
                            {item.nome_corretor}
                          </Nome>
                          <OpenSansMuted style={{ fontSize: '0.7rem' }}>
                            Corretor
                          </OpenSansMuted>
                        </div>
                        <button
                          onClick={() =>
                            toggleDetalhesCorretor(item.codigo_corretor)
                          }
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            marginLeft: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'transform 0.5s',
                          }}
                          aria-label={
                            aberto ? 'Ocultar detalhes' : 'Exibir detalhes'
                          }
                        >
                          <CaretDown
                            size={22}
                            style={{
                              transform: aberto
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                              transition: 'transform 0.5s',
                              color: '#293c66',
                            }}
                          />
                        </button>
                      </div>
                      {aberto && (
                        <>
                          <hr />
                          <div
                            className="d-flex align-items-center w-100"
                            style={{ gap: 24 }}
                          >
                            <div style={{ flex: 1 }}>
                              <OpenSansMuted
                                style={{ fontSize: '0.8rem', marginBottom: 2 }}
                              >
                                Dias sem Atendimento
                              </OpenSansMuted>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: '#293c66',
                                  fontSize: '1rem',
                                }}
                              >
                                {item.dias_sem_atendimento}
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <OpenSansMuted
                                style={{ fontSize: '0.8rem', marginBottom: 2 }}
                              >
                                Minutos sem Atendimento
                              </OpenSansMuted>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: '#293c66',
                                  fontSize: '1rem',
                                }}
                              >
                                {item.minutos_sem_atendimento}
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <OpenSansMuted
                                style={{ fontSize: '0.8rem', marginBottom: 2 }}
                              >
                                Máx. Fidelizações
                              </OpenSansMuted>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: '#293c66',
                                  fontSize: '1rem',
                                }}
                              >
                                {item.qtd_max_fidelizacao}
                              </div>
                            </div>
                          </div>
                          <div
                            className="row align-self-end mt-3 mr-2"
                            style={{ gap: 12 }}
                          >
                            <BotaoEditar
                              isEditando={isEditando}
                              onClick={() => {
                                setIsEditando(true);
                                setCorretorFormData({
                                  codigo_corretor: item.codigo_corretor,
                                  dias_sem_atendimento: Number(
                                    item.dias_sem_atendimento == null
                                      ? 0
                                      : item.dias_sem_atendimento
                                  ),
                                  tempo_sem_atendimento: Number(
                                    item.minutos_sem_atendimento == null
                                      ? 0
                                      : item.minutos_sem_atendimento
                                  ),
                                  qtd_fidelizados: Number(
                                    item.qtd_max_fidelizacao == null
                                      ? 0
                                      : item.qtd_max_fidelizacao
                                  ),
                                });
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                            />
                            <BotaoExcluir
                              onClick={() => {
                                setDeletarContexto('corretor');
                                setDeletarCodigo(item.codigo);
                                setShowConfirmacao(true);
                              }}
                              isEditando={isEditando}
                            />
                          </div>
                        </>
                      )}
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div>
                {parametrosCorretor.map(item => (
                  <Linha
                    key={item.codigo_corretor}
                    style={{ alignItems: 'center' }}
                  >
                    <div className="col-1">
                      <Avatar>{getInitials(item.nome_corretor)}</Avatar>
                    </div>
                    <div className="col-3">
                      <div className="row">
                        <OpenSansMuted>Corretor</OpenSansMuted>
                      </div>
                      <div className="row">
                        <div style={{ fontWeight: 600 }}>
                          {item.nome_corretor}
                        </div>
                      </div>
                    </div>
                    <div className="col-2">
                      <OpenSansMuted>Dias sem Atendimento</OpenSansMuted>
                      <div style={{ fontWeight: 600, color: '#293c66' }}>
                        {item.dias_sem_atendimento}
                      </div>
                    </div>
                    <div className="col-2">
                      <OpenSansMuted>Minutos sem Atendimento</OpenSansMuted>
                      <div style={{ fontWeight: 600, color: '#293c66' }}>
                        {item.minutos_sem_atendimento}
                      </div>
                    </div>
                    <div className="col-2">
                      <OpenSansMuted>Máx. Fidelizações</OpenSansMuted>
                      <div style={{ fontWeight: 600, color: '#293c66' }}>
                        {item.qtd_max_fidelizacao}
                      </div>
                    </div>
                    <div className="col-2">
                      <div
                        className="row"
                        style={{
                          display: 'flex',
                          gap: '6px',
                          alignItems: 'center',
                        }}
                      >
                        <BotaoEditar
                          onClick={() => {
                            setIsEditando(true);
                            setCorretorFormData({
                              codigo_corretor: item.codigo_corretor,
                              dias_sem_atendimento: Number(
                                item.dias_sem_atendimento == null
                                  ? 0
                                  : item.dias_sem_atendimento
                              ),
                              tempo_sem_atendimento: Number(
                                item.minutos_sem_atendimento == null
                                  ? 0
                                  : item.minutos_sem_atendimento
                              ),
                              qtd_fidelizados: Number(
                                item.qtd_max_fidelizacao == null
                                  ? 0
                                  : item.qtd_max_fidelizacao
                              ),
                            });

                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          isEditando={isEditando}
                        />
                        <BotaoExcluir
                          onClick={() => {
                            setDeletarContexto('empreendimento');
                            setDeletarCodigo(item.codigo);
                            setShowConfirmacao(true);
                          }}
                          isEditando={isEditando}
                        />
                      </div>
                    </div>
                  </Linha>
                ))}
              </div>
            ))}
        </Tab>
      </Tabs>
      {showConfirmacao && (
        <ModalConfirmacao
          show={showConfirmacao}
          title={'Atenção'}
          message={'Tem certeza que deseja excluir este registro?'}
          onClose={async decisao => {
            if (decisao) {
              await handleDelete();
            }
            setShowConfirmacao(false);
            setDeletarContexto(null);
            setDeletarCodigo(null);
          }}
        />
      )}
    </>
  );
}

const BotaoSalvar = ({ isSubmitting }) => (
  <Row className="justify-content-end">
    <Col md="auto">
      <Button
        type="submit"
        variant="primary"
        disabled={isSubmitting}
        className="w-100 d-flex flex-row align-items-center justify-content-center"
      >
        <FaSave className="me-1" /> &nbsp;
        <span className="ms-1">{isSubmitting ? 'Salvando...' : 'Salvar'}</span>
      </Button>
    </Col>
  </Row>
);

const BotaoEditar = ({ isEditando, onClick }) => (
  <Button
    className="btn btn-sm rounded-lg"
    style={{ background: '#FFBA1A' }}
    variant="ghost"
    title="Editar"
    disabled={isEditando}
    onClick={onClick}
  >
    <Pencil size={16} style={{ color: '#fff' }} />
  </Button>
);

const BotaoExcluir = ({ isEditando, onClick }) => (
  <Button
    size="sm"
    title="Excluir"
    variant="danger"
    className="rounded-lg"
    onClick={onClick}
    disabled={isEditando}
  >
    <XCircle size={16} />
  </Button>
);
